import { useEffect, useState } from 'react'

import type { ILocalAudioTrack } from 'agora-rtc-sdk-ng'

import { createMicrophoneAudioTrack } from '@core/agoraRTC/libs/utils'
import {
  microphone3ASSelector,
  microphoneIdSelector,
} from '@core/deviceSettings'
import { useGlobalStore } from '@core/hooks/useGlobalStore'

/**
 * Hook that creates and manages the microphone audio track.
 * It creates a new track on every change of the microphone id.
 *
 * @param allowed Whether to create the track or not.
 */
export function useMicrophone(allowed: boolean): ILocalAudioTrack | undefined {
  const [audioTrack, setAudioTrack] = useState<ILocalAudioTrack>()
  const microphoneId = useGlobalStore(microphoneIdSelector)
  const { microphoneAEC, microphoneAGC, microphoneANS } = useGlobalStore(
    microphone3ASSelector,
  )

  useEffect(() => {
    if (allowed) {
      createMicrophoneAudioTrack({
        microphoneId,
        encoderConfig: 'high_quality_stereo',
        AEC: microphoneAEC,
        AGC: microphoneAGC,
        ANS: microphoneANS,
      }).then(setAudioTrack)
    } else {
      setAudioTrack(undefined)
    }
  }, [allowed, microphoneId, microphoneAEC, microphoneAGC, microphoneANS])

  useEffect(() => {
    return () => {
      if (audioTrack) {
        audioTrack.close()
      }
    }
  }, [audioTrack])

  return audioTrack
}
